import { Link } from "react-router-dom";

import {
  formatSessionDateRange,
  formatSessionParticipants,
  getSessionDisplayTimes,
} from "~/providers/utils/formatSessionDateRange";
import {
  ZoomMeeting,
  ZoomSessionType,
} from "~/providers/utils/types/zoomMeetingTypes";
import { isZoomMeetingJoinable } from "~/providers/utils/zoom/zoomMeetingJoin";
import { SessionTypeSelect } from "./SessionTypeSelect";

interface PatientSessionHeaderProps {
  sessionsPath: string;
  session: Pick<
    ZoomMeeting,
    | "topic"
    | "scheduled_start_time"
    | "scheduled_end_time"
    | "duration"
    | "participant_names"
    | "session_type"
    | "join_url"
    | "status"
  >;
  onSessionTypeChange: (sessionType: ZoomSessionType | null) => void;
  isUpdatingSessionType?: boolean;
}

export const SessionHeader = ({
  sessionsPath,
  session,
  onSessionTypeChange,
  isUpdatingSessionType = false,
}: PatientSessionHeaderProps) => {
  const { startTime, endTime, duration } = getSessionDisplayTimes(session);

  return (
    <div className="flex flex-col gap-2">
      <Link to={sessionsPath} className="flex w-fit items-center text-gray-700">
        <span className="mr-1 text-lg">&larr;</span>
        <span>Back to all sessions</span>
      </Link>
      <h1 className="text-2xl font-semibold text-gray-70">
        {session.topic ?? "Session"}
      </h1>
      <div className="flex items-stretch gap-x-3 gap-y-3 text-gray-700">
        <p className="flex shrink-0 items-center whitespace-nowrap border-r pr-3">
          {formatSessionDateRange(startTime, endTime, duration)}
        </p>
        <p className="flex shrink-0 items-center whitespace-nowrap border-r pr-3">
          {formatSessionParticipants(session.participant_names)}
        </p>
        {isZoomMeetingJoinable(session) && (
          <p className="flex shrink-0 items-center whitespace-nowrap border-r pr-3">
            <a
              href={session.join_url!}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary-600 underline underline-offset-4"
            >
              Link
            </a>
          </p>
        )}
        <SessionTypeSelect
          value={session.session_type}
          isDisabled={isUpdatingSessionType}
          onChange={onSessionTypeChange}
        />
      </div>
    </div>
  );
};
